// Design tokens for global UI theme
// These map directly to Ant Design v5 theme tokens via ConfigProvider.theme.token
// This file is the SINGLE SOURCE OF TRUTH for brand colors.
// CSS variables in tailwind.css are synced from here via app.tsx useEffect.

// 1) Project base tokens (previously defined as a separate block in app.tsx)
export const projectBaseTokens = {
  // Typography
  fontFamily: 'Inter',
} as const;

// 2) Brand colors - SINGLE SOURCE OF TRUTH
export const brandColors = {
  // Primary blue (original Flexar theme)
  primary: '#1677ff', // hsl(217, 91%, 55%)
  primaryHover: '#4096ff',
  primaryBg: '#e6f4ff',

  // Error/Danger color (for delete buttons, error states)
  error: '#ff4d4f',
  errorHover: '#ff7875',

  // Layout backgrounds (matching new-home-page design)
  // Page background - slightly gray (0 0% 97%)
  background: '#f7f7f7',
  // Sidebar background - white
  sidebarBackground: '#ffffff',
  // Card background - white
  cardBackground: '#ffffff',
  // Flexar: поверхность блока кода в ответе модели. Цвета токенов языка задаёт схема
  // подсветки (это семантика языка, а не бренд), а фон и основной цвет — отсюда,
  // как и у остальных поверхностей.
  codeBackground: '#f0f0f0',
  codeText: '#444444',

  // Avatar palette (original multicolor)
  avatarGradient1: { from: '#4F6DEE', to: '#67BDF9' },
  avatarGradient2: { from: '#38A04D', to: '#93DCA2' },
  avatarGradient3: { from: '#C35F2B', to: '#EDB395' },
  avatarGradient4: { from: '#633897', to: '#CBA1FF' },
  avatarMarble: ['#42D7E7', '#478AF5', '#9B51E0', '#87CEEB', '#6B46C1'],
} as const;

// Dark mode brand color adjustments
export const brandColorsDark = {
  primary: '#4096ff', // slightly brighter for dark mode
  primaryHover: '#69b1ff',
  primaryBg: '#111a2c',

  // Error/Danger color (same for dark mode, can adjust if needed)
  error: '#ff4d4f',
  errorHover: '#ff7875',

  // Layout backgrounds for dark mode
  background: '#141414',
  sidebarBackground: '#1a1a1a',
  cardBackground: '#1f1f1f',
  // Flexar: тёмная поверхность блока кода — см. одноимённые токены светлой темы
  codeBackground: '#282c34',
  codeText: '#abb2bf',

  avatarGradient1: { from: '#4F6DEE', to: '#67BDF9' },
  avatarGradient2: { from: '#38A04D', to: '#93DCA2' },
  avatarGradient3: { from: '#C35F2B', to: '#EDB395' },
  avatarGradient4: { from: '#633897', to: '#CBA1FF' },
  avatarMarble: ['#42D7E7', '#478AF5', '#9B51E0', '#87CEEB', '#6B46C1'],
} as const;

// 4) Flexar: вертикальный ритм markdown-ответа. Ключи — имена CSS-переменных, которые
// `app.tsx` выставляет на корне; читает их миксин `.markdownProse()` в `less/mixins.less`.
// Ритм не зависит от темы, поэтому лежит одним набором, а не парой light/dark.
export const markdownRhythm = {
  '--markdown-block-gap': '12px',
  '--markdown-heading-gap-top': '20px',
  '--markdown-heading-gap-bottom': '8px',
  '--markdown-list-indent': '22px',
  '--markdown-item-gap': '2px',
  '--markdown-rule-gap': '16px',
} as const;

// 5) Safe shared tokens for both themes (do not break dark mode)
export const sharedTokens = {
  // Colors - use brandColors
  colorPrimary: brandColors.primary,
  colorLink: brandColors.primary,
  colorPrimaryHover: brandColors.primaryHover,
  colorPrimaryBg: brandColors.primaryBg,
  colorError: brandColors.error,
  colorErrorHover: brandColors.errorHover,

  // Typography scale
  fontSize: 14,
  fontSizeLG: 16,

  // Ant Design controls/shape
  borderRadiusLG: 8,
  controlHeightLG: 40,
  lineWidth: 1,
} as const;

// Light-only overrides (safe to apply only in light theme)
export const lightOverrides = {
  colorTextBase: '#18181d',
  colorTextTertiary: '#18181d73', // rgba(24,24,29,0.45)
  colorBorderSecondary: '#eaeaea',
  colorFillSecondary: '#18181d0f', // rgba(24,24,29,0.06)
} as const;

// Dark theme overrides
export const darkOverrides = {
  colorPrimary: brandColorsDark.primary,
  colorLink: brandColorsDark.primary,
  colorPrimaryHover: brandColorsDark.primaryHover,
  colorPrimaryBg: brandColorsDark.primaryBg,
  colorError: brandColorsDark.error,
  colorErrorHover: brandColorsDark.errorHover,
} as const;

export const designTokensLight = {
  ...projectBaseTokens,
  ...sharedTokens,
  ...lightOverrides,
} as const;

export const designTokensDark = {
  ...projectBaseTokens,
  ...sharedTokens,
  ...darkOverrides,
} as const;

// Back-compat default (light)
export const designTokens = designTokensLight;

export const componentTokens = {
  Menu: {
    light: {
      itemSelectedColor: '#ffffff',
    },
    dark: {
      itemSelectedColor: '#ffffff',
    },
  },
} as const;

// Flexar: shared focus treatment; each theme supplies its base elevation.
const chatSurfaceEffects = {
  '--chat-composer-focus-shadow':
    '0 0 8px 1px color-mix(in srgb, var(--accent-primary) 12%, transparent), var(--chat-composer-shadow)',
} as const;

// Flexar: subtle light surfaces for the chat workspace, derived from the active brand where tinted.
export const chatSurfacesLight = {
  // A restrained brand tint on outer and assistant-navigation surfaces.
  '--chat-page': 'color-mix(in srgb, #f3f3f3 99%, hsl(var(--primary)) 1%)',
  '--chat-panel': '#fefefe',
  '--chat-drawer': 'color-mix(in srgb, #f8f8f8 99%, hsl(var(--primary)) 1%)',
  '--chat-composer': '#ffffff',
  ...chatSurfaceEffects,
  // Flexar: две слабые тени отделяют белый ввод от почти белой панели без тяжёлой рамки.
  '--chat-composer-shadow':
    '0 2px 6px rgb(0 0 0 / 2%), 0 8px 28px rgb(0 0 0 / 3%)',
  // Flexar: рамка ввода слабее разделителей навигации, чтобы не перетягивать внимание с текста.
  '--chat-input-border': '#e8e8e8',
  '--chat-divider': '#e3e3e3',
} as const;

// Retain the existing dark hierarchy, including custom-brand background tokens.
export const chatSurfacesDark = {
  '--chat-page': 'hsl(var(--background))',
  '--chat-panel': 'hsl(var(--card))',
  '--chat-drawer': 'hsl(var(--card))',
  '--chat-composer': 'hsl(var(--background))',
  ...chatSurfaceEffects,
  '--chat-composer-shadow': '0 0 #0000',
  '--chat-input-border': 'var(--input-border)',
  '--chat-divider': 'hsl(var(--border))',
} as const satisfies ChatSurfaceTokens;

// Keep theme surface contracts identical as new tokens are added.
type ChatSurfaceTokens = { [K in keyof typeof chatSurfacesLight]: string };
